import dayjs from "dayjs";
import { useEffect, useRef } from "react";
import { UserRole } from "tm-shared/types";

import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { saveProgressToServerThunk } from "@/store/thunks";
import { isManagerRole } from "@/utils";

interface ProgressSyncCapabilities {
    canAutoSyncProgress: boolean;
}

const getProgressSyncCapabilities = (
    role: UserRole,
): ProgressSyncCapabilities => ({
    canAutoSyncProgress: !isManagerRole(role),
});

const SYNC_INTERVAL_MS = 60 * 1000;

export const useSaveStoreToServer = () => {
    const offlineMode = useOfflineModeSelector();

    const isInitialDataLoading = useAppSelector(
        (s) => s.offline.isInitialDataLoading,
    );
    const role = useProtectedUserRoleSelector();
    const { lastModifiedAt, lastSyncedAt } = useAppSelector(
        (s) => s.offline.progress,
    );

    const { canAutoSyncProgress } = getProgressSyncCapabilities(role);

    const dispatch = useAppDispatch();
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!canAutoSyncProgress) return;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if (
            offlineMode ||
            !lastModifiedAt ||
            dayjs(lastSyncedAt).isAfter(dayjs(lastModifiedAt)) ||
            isInitialDataLoading
        ) {
            return;
        }

        const lastSyncTime = dayjs(lastSyncedAt);
        const timeSinceLastSync = dayjs().diff(lastSyncTime, "milliseconds");

        const timeUntilNextSync = Math.max(
            0,
            SYNC_INTERVAL_MS - timeSinceLastSync,
        );

        timeoutRef.current = setTimeout(() => {
            dispatch(saveProgressToServerThunk());
        }, timeUntilNextSync);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [
        offlineMode,
        lastModifiedAt,
        lastSyncedAt,
        role,
        dispatch,
        isInitialDataLoading,
        canAutoSyncProgress,
    ]);
};
